import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosResponse } from "axios";
import React, { memo, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { savePdfUrls } from "../../../Redux/Actions/Index";
import RequisitionType from "../../../Services/Requisition/RequisitionTypeService";
import LoadButton from "../../../Shared/Common/LoadButton";
import { Loader } from "../../../Shared/Common/Loader";
import PermissionComponent, {
  getPermissionDisplayName,
  PermissionObject,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import { useResultDataContext } from "../../../Shared/OncologyResultDataContext";
import useLang from "Shared/hooks/useLanguage";

interface ExpandableI {
  row: any;
  requisitionId: number;
  reqTypeId: number;
  requisitionOrderId: number;
  facilityId: number;
  panelId: number;
}

export const ampStatusDropDown = [
  {
    value: "amp",
    label: "Amp",
  },
  {
    value: "no amp",
    label: "No Amp",
  },
];

const isProsTestPanelName = (name: string) =>
  /prostest|pros\s*test/i.test((name ?? "").trim());

const ResultDataExpandableRow = (props: ExpandableI) => {
  const {
    row,
    requisitionId,
    reqTypeId,
    requisitionOrderId,
    facilityId,
    panelId,
  } = props;

  const initialMainCheckboxes = {
    controlCheckbox: [],
    organismCheckbox: [],
    resistanceCheckbox: [],
  };

  const {
    data,
    loadGridData,
    filterData,
    rowsToExpand,
    setRowsToExpand,
    isMasterExpandTriggered,
  } = useResultDataContext();

  const { t } = useLang();

  const dispatch = useDispatch();
  const rowExpandKey = `${row?.requisitionOrderId}_${row?.panelID}`;
  const [bar, setBar] = useState(false);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isApiPanel, setIsApiPanel] = useState(false);
  const [isSentToApi, setIsSentToApi] = useState(false);
  const [isProsPanel, setIsProsPanel] = useState(false);
  const [finalResult, setFinalResult] = useState<any>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSendingToApi, setIsSendingToApi] = useState(false);
  const [mControlsList, setMControlsList] = useState<any>([]);
  const [isReRunCheckBox, setIsReRunCheckBox] = useState(false);
  const [mPathogensList, setMPathogensList] = useState<any>([]);
  const [isManualEditMode, setIsManualEditMode] = useState(false);
  const [showCtValueError, setShowCtValueError] = useState(false);
  const [mainCheckboxes, setMainCheckboxes] = useState(initialMainCheckboxes);
  const [showEditFinalScoreModal, setShowEditFinalScoreModal] = useState(false);
  const [resultSummaryRows, setResultSummaryRows] = useState([
    {
      fieldKey: "isNettest20Score",
      test: "NETest 2.0 Score",
      result: "",
      interpretation: "",
    },
    {
      fieldKey: "isProgressionScore",
      test: "Progression Score",
      result: "",
      interpretation: "",
    },
    {
      fieldKey: "isPPQScore",
      test: "PPQ Score",
      result: "",
      interpretation: "",
    },
  ]);
  const targetRef = useRef(null);
  const [reportNotes, setReportNotes] = useState<any>({
    lisNotes: "",
  });
  const [reportCheckboxes, setReportCheckboxes] = useState({
    isAmended: false,
  });
  const [fieldStatus, setFieldStatus] = useState<any>({
    isNettest20Score: false,
    isProgressionScore: false,
    isPPQScore: false,
  });

  const allowedKeys: string[] = [
    "Backspace",
    "Tab",
    "+",
    "-",
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
  ];
  const ModalhandleClose = () => setShow(false);

  const normalizeLisStatus = (s: string | undefined) =>
    (s ?? "").trim().toLowerCase();
  const LIS_READY_TO_VALIDATE = "ready to validate";

  const isNumericKey = (key: string): boolean => {
    return /^[0-9.]$/.test(key) || allowedKeys.includes(key);
  };

  const ChangeOrganismStatus = (
    status: string,
    item: any,
    itemIndex: number
  ): void => {
    const shouldUpdateCtValue = (
      ctValue: string | null | undefined,
      status: string
    ): boolean => {
      if (status === "Invalid") {
        return true; // Update all values for Invalid
      }
      return ctValue === "" || ctValue === null; // Only update empty/null for other statuses
    };

    const updateCtValues = (items: any[], newCtValue: string): any[] => {
      return items?.map((data: any) => ({
        ...data,
        ctValue: shouldUpdateCtValue(data.ctValue, newCtValue)
          ? newCtValue
          : data.ctValue,
      }));
    };

    const updatePathogensListLocally = (newCtValue: string): void => {
      const updatedPathogensList = [...mPathogensList];
      const currentItem = updatedPathogensList[itemIndex];

      updatedPathogensList[itemIndex] = {
        ...currentItem,
        resistance: updateCtValues(currentItem.resistance, newCtValue),
        organisms: updateCtValues(currentItem.organisms, newCtValue),
      };

      setMPathogensList(updatedPathogensList);
    };

    const updateStatusViaApi = async (): Promise<void> => {
      const requestPayload = {
        status,
        pathogens: item,
      };

      try {
        const response =
          await RequisitionType.ChangeOrganismStatusOncology(requestPayload);

        if (response?.data?.statusCode === 200) {
          toast.success(t(response.data.message));
          await GetExpandDataById(requisitionOrderId, panelId);
        }
      } catch (error) {
        console.error("Error changing organism status:", error);
      }
    };

    // Main logic
    if (status === "Undetermined" || status === "Invalid") {
      updatePathogensListLocally(status);
    } else {
      updateStatusViaApi();
    }
  };

  const SendResultToApi = () => {
    const foundRecord = data?.gridData?.data?.data?.find(
      (item: any) =>
        item?.requisitionOrderId === requisitionOrderId &&
        item?.panelID === panelId
    );
    const lisStatus = normalizeLisStatus(foundRecord?.lisStatus);
    if (lisStatus !== "ready for api" && lisStatus !== "sent to api") {
      return toast.error(t("Status is not ready for API"));
    }
    setIsSendingToApi(true);

    const payload = {
      panelId: panelId,
      requisitionOrderId: requisitionOrderId,
    };
    RequisitionType.SendOncologyResultToApi(payload)
      .then((res: AxiosResponse) => {
        if (res?.data?.statusCode === 200) {
          toast.success(t(res?.data?.message));
          loadGridData(false);
          GetExpandDataById(requisitionOrderId, panelId);
        } else if (res?.data?.statusCode === 400) {
          toast.error(t(res?.data?.message));
        }
      })
      .catch((err: any) => {
        console.error(err);
        toast.error(t("Something went wrong while sending to API."));
      })
      .finally(() => {
        setIsSendingToApi(false);
      });
  };

  const SaveIdResultDataExpand = () => {
    const getSummaryResult = (fieldKey: string) =>
      resultSummaryRows.find((item: any) => item.fieldKey === fieldKey)
        ?.result ?? "";

    const row = {
      requisitionOrderId: requisitionOrderId,
      controls: mControlsList,
      pathogens: mPathogensList,
      lisNotes: reportNotes?.lisNotes,
      criticalReport: reportNotes?.criticalReport,
      ccFaxNumbers: "",
      panelId: panelId,
      finalResult: finalResult,
      isSentToApi: isSentToApi,
      netestScore: getSummaryResult("isNettest20Score"),
      progressionScore: getSummaryResult("isProgressionScore"),
      ppqScore: getSummaryResult("isPPQScore"),
    };
    setIsSubmitting(true);
    RequisitionType.SaveIdResultDataExpandOncology(row)
      .then(async (res: AxiosResponse) => {
        if (res?.data.statusCode === 200) {
          toast.success(t(res?.data?.message));
          setIsSubmitting(false);
          await loadGridData(false);
          GetExpandDataById(requisitionOrderId, panelId);
        }
      })
      .catch((err: any) => {
        setIsSubmitting(false);
        console.error(err);
      });
  };

  const ApplyRerun = () => {
    const row = {
      requisitionOrderId: requisitionOrderId,
      reqTypeId: reqTypeId,
      panelId: panelId,
      controls: mControlsList,
      pathogens: mPathogensList,
    };
    setIsReRunCheckBox(true);
    RequisitionType.ApplyRerunOncology(row)
      .then(async (res: AxiosResponse) => {
        if (res?.data.statusCode === 200) {
          toast.success(t(res?.data?.message));
          await loadGridData(false);
          GetExpandDataById(requisitionOrderId, panelId);
        } else {
          toast.error(t(res?.data?.message ?? "Request failed"));
        }
      })
      .catch((err: any) => {
        console.error(err);
        toast.error(
          t(
            err?.response?.data?.message ??
              "Something went wrong during re-run."
          )
        );
      })
      .finally(() => {
        setIsReRunCheckBox(false);
        setBar(false);
        setMainCheckboxes(initialMainCheckboxes);
      });
  };

  const GenerateBlanksAgainstReqOrderId = () => {
    RequisitionType.GenerateBlanksAgainstReqOrderId(requisitionOrderId)
      .then((res: AxiosResponse) => {
        if (res?.data.statusCode === 200) {
          toast.success(t(res?.data?.message));
          GetExpandDataById(requisitionOrderId, panelId);
        }
      })
      .catch((err: any) => {
        console.error(err);
      });
  };

  const handleFieldChange = (
    panelIndex: any,
    Index: any,
    field: any,
    value: any,
    type: string
  ) => {
    const updatedPathogensList = [...mPathogensList];
    if (type === "resistance") {
      const resistanceToUpdate =
        updatedPathogensList[panelIndex]?.resistance?.[Index];
      if (resistanceToUpdate) {
        if (field === "isReRun") {
          setMainCheckboxes((pre: any) => {
            return {
              ...pre,
              resistanceCheckbox: [
                ...pre.resistanceCheckbox,
                resistanceToUpdate.id,
              ],
            };
          });
        }
        resistanceToUpdate[field] = value;
        if (field === "isReRun" && value === false) {
          setMainCheckboxes((prev: any) => {
            return {
              ...prev,
              resistanceCheckbox: prev.resistanceCheckbox.filter(
                (id: any) => id !== resistanceToUpdate.id
              ),
            };
          });
        }
      }
    } else {
      const organismToUpdate =
        updatedPathogensList[panelIndex]?.organisms?.[Index];
      if (organismToUpdate) {
        if (field === "isReRun") {
          setMainCheckboxes((pre: any) => {
            return {
              ...pre,
              organismCheckbox: [...pre.organismCheckbox, organismToUpdate.id],
            };
          });
        }
        organismToUpdate[field] = value === "" ? null : value;
        if (field === "isReRun" && value === false) {
          setMainCheckboxes((prev: any) => {
            return {
              ...prev,
              organismCheckbox: prev.organismCheckbox.filter(
                (id: any) => id !== organismToUpdate.id
              ),
            };
          });
        }
      }
    }

    setMPathogensList(updatedPathogensList);
  };

  const OncologyLISReportView = async (
    reqId: any,
    reqType: any,
    facilityId: any,
    requisitionOrderId: any,
    isPreview = true
  ): Promise<AxiosResponse | undefined> => {
    const row = {
      reqId: reqId,
      requisitionOrderId: requisitionOrderId,
      templateId: "",
      reqType: reqType,
      facilityId: facilityId,
      isPreview,
      panelId: panelId,
      isAmended: reportCheckboxes?.isAmended,
    };

    if (isPreview) {
      setIsPreviewing(true);
    }

    try {
      const res: AxiosResponse =
        await RequisitionType.OncologyLISReportView(row);
      if (res?.data?.statusCode === 200) {
        if (isPreview) {
          toast.success(t(res?.data?.message));
          dispatch(savePdfUrls(res?.data?.data));
          window.open("/docs-viewer", "_blank", "noreferrer");
        }
      } else if (res?.data?.statusCode === 400) {
        toast.error(t(res?.data?.message));
      }
      return res;
    } catch (err: any) {
      console.error(err);
      if (!isPreview) {
        toast.error(t("Something went wrong while generating the report."));
      }
    } finally {
      if (isPreview) {
        setIsPreviewing(false);
      }
    }
  };

  const Validate = async (
    reqId: any,
    reqType: any,
    facilityId: any,
    requisitionOrderId: any
  ) => {
    setIsValidating(true);
    try {
      const foundRecord = data?.gridData?.data?.data?.find(
        (item: any) =>
          item?.requisitionId === reqId &&
          item?.requisitionOrderId === requisitionOrderId &&
          item?.panelID === panelId
      );
      if (!foundRecord) {
        toast.error(t("Record not found"));
        return;
      }
      const lisStatus = normalizeLisStatus(foundRecord?.lisStatus);
      if (lisStatus === "pending" || lisStatus === "pending abr") {
        return toast.error(t("Result is not completed in all tests"));
      }
      if (lisStatus === "pending pharmd") {
        return toast.error(t("Result is not completed due to Pending PharmD"));
      }
      if (lisStatus !== LIS_READY_TO_VALIDATE) {
        return toast.error(t("Status is not ready to validate"));
      }
      const publishObj = {
        requisitionOrderId: requisitionOrderId,
        panelId: panelId,
        isAmended: reportCheckboxes?.isAmended,
      };
      const publishRes: AxiosResponse =
        await RequisitionType.ValidateOncology(publishObj);
      if (publishRes?.data?.statusCode === 200) {
        toast.success(t(publishRes?.data?.message));
        await loadGridData(false);
        GetExpandDataById(requisitionOrderId, panelId);
      } else if (publishRes?.data?.statusCode === 400) {
        toast.info(t(publishRes?.data?.message));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("Something went wrong during validation."));
    } finally {
      setIsValidating(false);
    }
  };

  const Publish = async (
    reqId: any,
    reqType: any,
    facilityId: any,
    requisitionOrderId: any
  ) => {
    setIsPublishing(true);
    try {
      const foundRecord = data?.gridData?.data?.data?.find(
        (item: any) =>
          item?.requisitionId === reqId &&
          item?.requisitionOrderId === requisitionOrderId &&
          item?.panelID === panelId
      );
      if (!foundRecord) {
        toast.error(t("Record not found"));
        return;
      }
      const lisStatus = normalizeLisStatus(foundRecord?.lisStatus);
      if (lisStatus === "pending" || lisStatus === "pending abr") {
        return toast.error(t("Result is not completed in all tests"));
      }
      if (lisStatus === "pending pharmd") {
        return toast.error(t("Result is not completed due to Pending PharmD"));
      }
      // if (lisStatus !== LIS_READY_TO_PUBLISH) {
      //   return toast.error(t("Status is not ready to publish"));
      // }
      const publishObj = {
        requisitionOrderId: requisitionOrderId,
        panelId: panelId,
        isAmended: reportCheckboxes?.isAmended,
      };
      const publishRes: AxiosResponse =
        await RequisitionType.PublishOncology(publishObj);
      if (publishRes?.data?.statusCode === 200) {
        toast.success(t(publishRes?.data?.message));
        await OncologyLISReportView(
          reqId,
          reqType,
          facilityId,
          requisitionOrderId,
          false
        );
        loadGridData(false);
      } else if (publishRes?.data?.statusCode === 400) {
        toast.info(t(publishRes?.data?.message));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("Something went wrong during publishing."));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSetReRunForAll = (
    value: boolean,
    itemIndex: number,
    type: string
  ) => {
    const updatedPathogensList = [...mPathogensList];
    if (updatedPathogensList[itemIndex]) {
      if (type === "resistance") {
        updatedPathogensList[itemIndex].resistance = updatedPathogensList[
          itemIndex
        ].resistance?.map((data: any) => ({
          ...data,
          isReRun: value,
        }));

        if (value) {
          setMainCheckboxes((pre: any) => {
            return {
              ...pre,
              resistanceCheckbox: updatedPathogensList[
                itemIndex
              ].resistance.map((item: any) => item.id),
            };
          });
        } else {
          setMainCheckboxes((pre: any) => {
            return {
              ...pre,
              resistanceCheckbox: [],
            };
          });
        }
      } else {
        updatedPathogensList[itemIndex].organisms = updatedPathogensList[
          itemIndex
        ].organisms?.map((data: any) => ({
          ...data,
          isReRun: value,
        }));

        if (value) {
          setMainCheckboxes((pre: any) => {
            return {
              ...pre,
              organismCheckbox: updatedPathogensList[itemIndex].organisms?.map(
                (item: any) => item.id
              ),
            };
          });
        } else {
          setMainCheckboxes((pre: any) => {
            return {
              ...pre,
              organismCheckbox: [],
            };
          });
        }
      }

      // Update the state with the modified pathogens list
      setMPathogensList(updatedPathogensList);
    }
  };

  const isValidScoreInput = (value: string) => {
    if (value === "") return true;
    if (!/^\d*\.?\d*$/.test(value)) return false;
    const num = parseFloat(value);
    return !Number.isNaN(num) && num >= 0 && num <= 100;
  };

  const handleResultSummaryChange = (fieldKey: string, value: string) => {
    if (!isValidScoreInput(value)) return;
    if (isApiPanel && isManualEditMode) {
      setIsSentToApi(false);
    }
    setResultSummaryRows((prev) =>
      prev.map((row: any) =>
        row.fieldKey === fieldKey ? { ...row, result: value } : row
      )
    );
  };

  // Start IXpress condtion on the base on Upload
  const permissions = useSelector(
    (state: any) =>
      state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.permissions ||
      []
  );

  const hasPermission = permissions.some(
    (permission: PermissionObject) =>
      permission.subject.replace(/\n/g, "").toLowerCase() ===
        "Result Data".replace(/\n/g, "").toLowerCase() &&
      permission.moduleName.replace(/\n/g, "").toLowerCase() ===
        "ID LIS".replace(/\n/g, "").toLowerCase() &&
      permission.action.replace(/\n/g, "").toLowerCase() ===
        "FileUpload".replace(/\n/g, "").toLowerCase()
  );
  // End IXpress condtion on the base on Upload

  const GetExpandDataById = (requisitionOrderId: any, panelId: any) => {
    setLoading(true);
    const obj = {
      panelId: panelId,
      requisitionOrderId: requisitionOrderId,
    };
    RequisitionType.GetOncologyExpandDataById(obj)
      .then((res: any) => {
        const responseData = res?.data?.data ?? {};
        const panelName =
          responseData?.pathogens?.[0]?.panelName ?? row?.panelName ?? "";
        const prosPanelByName = isProsTestPanelName(panelName);
        const showNetestScore = !!responseData?.isShowNetestScore;

        setFinalResult(responseData?.finalResult);
        setIsProsPanel(prosPanelByName);
        setIsApiPanel(showNetestScore && !prosPanelByName);
        setIsManualEditMode(false);
        setIsSentToApi(responseData?.isSentToApi);
        setFieldStatus({
          isNettest20Score: prosPanelByName || responseData?.isShowNetestScore,
          isProgressionScore:
            !prosPanelByName && responseData?.isShowProgressionScore,
          isPPQScore: !prosPanelByName && responseData?.isShowPPQScore,
        });
        setReportCheckboxes({
          isAmended: res?.data?.data?.isAmended,
        });
        setResultSummaryRows((prev: any) =>
          prev.map((summaryRow: any) => {
            if (summaryRow.fieldKey === "isNettest20Score") {
              return {
                ...summaryRow,
                result: responseData?.netestScore ?? "",
                interpretation: responseData?.netestInterpretation ?? "",
              };
            }
            if (summaryRow.fieldKey === "isProgressionScore") {
              return {
                ...summaryRow,
                result: responseData?.progressionScore ?? "",
                interpretation: responseData?.progressionInterpretation ?? "",
              };
            }
            if (summaryRow.fieldKey === "isPPQScore") {
              return {
                ...summaryRow,
                result: responseData?.ppqScore ?? "",
                interpretation: responseData?.ppqInterpretation ?? "",
              };
            }
            return summaryRow;
          })
        );
        setMControlsList(res?.data?.data?.controls);
        setMPathogensList(res?.data?.data?.pathogens);
        setReportNotes({
          lisNotes: res?.data?.data?.lisNotes,
        });
        setLoading(false);
      })
      .catch((err: any) => {
        console.error(err, "err");
        setLoading(false);
      });
  };

  useEffect(() => {
    setMControlsList([]);
    setMPathogensList([]);
    setMainCheckboxes(initialMainCheckboxes);
    setFinalResult("");
    setIsManualEditMode(false);
    setIsApiPanel(false);
    setIsProsPanel(false);
    setIsSentToApi(false);
    setReportNotes({ lisNotes: "" });
    setResultSummaryRows([
      {
        fieldKey: "isNettest20Score",
        test: "NETest 2.0 Score",
        result: "",
        interpretation: "",
      },
      {
        fieldKey: "isProgressionScore",
        test: "Progression Score",
        result: "",
        interpretation: "",
      },
      {
        fieldKey: "isPPQScore",
        test: "PPQ Score",
        result: "",
        interpretation: "",
      },
    ]);
    GetExpandDataById(requisitionOrderId, panelId);
  }, [requisitionOrderId, panelId, filterData.tabId]);

  useEffect(() => {
    const { organismCheckbox, resistanceCheckbox, controlCheckbox } =
      mainCheckboxes;
    setBar(
      organismCheckbox.length > 0 ||
        resistanceCheckbox.length > 0 ||
        controlCheckbox.length > 0
    );
  }, [mainCheckboxes]);

  useEffect(() => {
    if (!targetRef.current || !isMasterExpandTriggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // setIsVisible(true);
          const allData = data.gridData.data.data;
          const lastElement = String(rowsToExpand[rowsToExpand.length - 1]);

          const lastIndex = allData.findIndex(
            (item: any) =>
              `${item.requisitionOrderId}_${item.panelID}` === lastElement
          );

          // Get next 5 elements after lastElement
          const nextFiveIds = allData
            .slice(lastIndex + 1, lastIndex + 4)
            .map((item: any) => `${item.requisitionOrderId}_${item.panelID}`);

          setRowsToExpand((prev: any) => [...prev, ...nextFiveIds]);

          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    observer.observe(targetRef.current);

    return () => {
      observer.disconnect();
    };
  }, [targetRef.current]);

  const hasCtValue = (value: any) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const isProsPanelActive =
    isProsPanel ||
    isProsTestPanelName(mPathogensList?.[0]?.panelName ?? row?.panelName ?? "");

  const isCtScoreCompletedInAllPanels =
    mPathogensList?.length > 0 &&
    mPathogensList.every((panel: any) => {
      const organismRows = panel?.organisms ?? [];
      const rowsToCheck = isProsPanelActive
        ? organismRows
        : [...organismRows, ...(panel?.resistance ?? [])];

      if (rowsToCheck.length === 0) return false;

      return rowsToCheck.every((ctRow: any) => hasCtValue(ctRow?.ctValue));
    });

  const visibleResultSummaryRows = resultSummaryRows.filter(
    (summaryRow: any) => fieldStatus?.[summaryRow.fieldKey]
  );

  const getResultSummaryTestLabel = (summaryRow: any) => {
    if (summaryRow.fieldKey === "isNettest20Score" && isProsPanelActive) {
      return "PROSTest Genes";
    }
    return summaryRow.test;
  };

  const getRowLisStatus = () => {
    const foundRecord = data?.gridData?.data?.data?.find(
      (item: any) =>
        item?.requisitionOrderId === requisitionOrderId &&
        item?.panelID === panelId
    );
    return normalizeLisStatus(foundRecord?.lisStatus ?? row?.lisStatus);
  };

  const isReadyForApiWorkflow = () => {
    const status = getRowLisStatus();
    return status === "ready for api" || status === "sent to api";
  };

  const sendToApiButtonText =
    getRowLisStatus() === "sent to api" ? t("Resend to Api") : t("Send to Api");

  const handleEditFinalScore = () => {
    if (!isCtScoreCompletedInAllPanels) {
      setShowCtValueError(true);
      toast.error(t("CT score is not completed in all panels"));
      return;
    }
    if (!isReadyForApiWorkflow()) {
      toast.error(t("Status is not ready for API"));
      return;
    }
    setShowCtValueError(false);
    setShowEditFinalScoreModal(true);
  };

  const handleFieldChangeTextArea = (value: any) => {
    setReportNotes((prev: any) => ({ ...prev, lisNotes: value }));
  };

  const handleCheckboxChange = (value: boolean) => {
    // Update the state with the new checkbox value
    setReportCheckboxes({ isAmended: value });
  };

  const confirmEditFinalScore = () => {
    setIsManualEditMode(true);
    setIsSentToApi(false);
    setShowEditFinalScoreModal(false);
  };
  const isResultFieldEditable = (() => {
    if (filterData.tabId !== 1) return false;
    const prosPanel = isProsPanelActive;
    if (!prosPanel && isApiPanel) return isManualEditMode;
    return isCtScoreCompletedInAllPanels;
  })();
  return (
    <div
      ref={
        rowsToExpand.length > 0 &&
        rowExpandKey === String(rowsToExpand[rowsToExpand.length - 2])
          ? targetRef
          : null
      }
      className="d-flex flex-column flex-column-fluid table-expend-sticky table-expend-sm-sticky"
    >
      <div id="kt_app_content" className="app-content flex-column-fluid pb-0">
        <div
          id="kt_app_content_container"
          className="app-container container-fluid mb-container"
        >
          <div className="d-flex flex-wrap gap-3 justify-content-center justify-content-sm-between align-items-center mt-3">
            <div className="align-items-center d-flex mb-md-3 mb-3 gap-2 flex-wrap">
              {filterData.tabId === 1 ||
              (filterData.tabId === 2 && reportCheckboxes.isAmended) ? (
                <div className="d-flex gap-2">
                  <PermissionComponent
                    moduleName="Oncology LIS"
                    pageName="Result Data"
                    permissionIdentifier="Publish"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <LoadButton
                        id={`ResultDataExpandPublish`}
                        className="btn btn-success btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                        loading={isPublishing}
                        btnText={getPermissionDisplayName(
                          pageName,
                          moduleName,
                          permissionIdentifier,
                          "Publish"
                        )}
                        loadingText={t("Processing.....")}
                        onClick={async () => {
                          if (
                            hasPermission === true &&
                            row?.graphImageFileBit === false
                          ) {
                            setShow(true);
                          } else {
                            await Publish(
                              requisitionId,
                              reqTypeId,
                              facilityId,
                              row?.requisitionOrderId
                            );
                          }
                        }}
                      />
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Oncology LIS"
                    pageName="Result Data"
                    permissionIdentifier="Validate"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <LoadButton
                        id={`ResultDataExpandValidate`}
                        className="btn btn-success btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                        loading={isValidating}
                        btnText={getPermissionDisplayName(
                          pageName,
                          moduleName,
                          permissionIdentifier,
                          "Validate"
                        )}
                        loadingText={t("Processing.....")}
                        onClick={async () => {
                          if (
                            hasPermission === true &&
                            row?.graphImageFileBit === false
                          ) {
                            setShow(true);
                          } else {
                            await Validate(
                              requisitionId,
                              reqTypeId,
                              facilityId,
                              row?.requisitionOrderId
                            );
                          }
                        }}
                      />
                    )}
                  </PermissionComponent>
                </div>
              ) : null}
              <PermissionComponent
                moduleName="Oncology LIS"
                pageName="Result Data"
                permissionIdentifier="Preview"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <LoadButton
                    id={`ResultDataExpandPreviewButton`}
                    className="btn btn-warning btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500 text-white"
                    loading={isPreviewing}
                    btnText={getPermissionDisplayName(
                      pageName,
                      moduleName,
                      permissionIdentifier,
                      "Preview"
                    )}
                    loadingText={t("Previewing")}
                    onClick={() =>
                      OncologyLISReportView(
                        requisitionId,
                        reqTypeId,
                        facilityId,
                        row.requisitionOrderId
                      )
                    }
                  />
                )}
              </PermissionComponent>
              <PermissionComponent
                moduleName="Oncology LIS"
                pageName="Result Data"
                permissionIdentifier="Save"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <LoadButton
                    id={`ResultDataExpandSaveButton`}
                    className="btn btn-primary btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                    loading={isSubmitting}
                    btnText={getPermissionDisplayName(
                      pageName,
                      moduleName,
                      permissionIdentifier,
                      "Save"
                    )}
                    loadingText={t("Saving")}
                    onClick={() => SaveIdResultDataExpand()}
                  />
                )}
              </PermissionComponent>
              {filterData.tabId === 1 ? (
                <div className="d-flex flex-column">
                  <div className="d-flex gap-2">
                    {isApiPanel && !isProsPanelActive ? (
                      <PermissionComponent
                        moduleName="Oncology LIS"
                        pageName="Result Data"
                        permissionIdentifier="SendtoAPI"
                      >
                        <LoadButton
                          id="ResultDataExpandSendToApi"
                          className="btn btn-outline-primary btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                          loading={isSendingToApi}
                          btnText={sendToApiButtonText}
                          loadingText={t("Sending...")}
                          onClick={() => SendResultToApi()}
                        />
                      </PermissionComponent>
                    ) : null}
                    {isApiPanel && !isProsPanelActive ? (
                      <PermissionComponent
                        moduleName="Oncology LIS"
                        pageName="Result Data"
                        permissionIdentifier="EditFinalScore"
                      >
                        {({ pageName, moduleName, permissionIdentifier }) => (
                          <button
                            id="ResultDataExpandEditFinalScore"
                            className="btn btn-warning btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                            onClick={handleEditFinalScore}
                            disabled={
                              !isCtScoreCompletedInAllPanels ||
                              !isReadyForApiWorkflow()
                            }
                          >
                            {getPermissionDisplayName(
                              pageName,
                              moduleName,
                              permissionIdentifier,
                              "Edit Final Score"
                            )}
                          </button>
                        )}
                      </PermissionComponent>
                    ) : null}
                  </div>
                  {showCtValueError && !isCtScoreCompletedInAllPanels ? (
                    <span className="text-danger fs-12px mt-1">
                      {t("CT value is not completed in all panels")}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {bar ? (
                <LoadButton
                  className="btn btn-info btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                  loading={isReRunCheckBox}
                  btnText={t("Apply ReRun")}
                  loadingText={t("Applying")}
                  onClick={() => ApplyRerun()}
                />
              ) : null}
              {filterData.tabId === 2 ? (
                <>
                  <label className="form-check form-check-inline form-check-solid m-0 fw-500">
                    <input
                      id="ResultDataExpandAmendedReportCheckBox"
                      className="form-check-input"
                      type="checkbox"
                      checked={reportCheckboxes.isAmended}
                      onChange={(e) => handleCheckboxChange(e.target.checked)}
                    />
                    {t("Amended Report")}
                  </label>
                </>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3 mb-2">
              {loading ? null : mPathogensList?.length === 0 ? (
                <button
                  id="ResultDataExpandCreatManualResult"
                  onClick={() => GenerateBlanksAgainstReqOrderId()}
                  className="btn btn-linkedin btn-sm fs-12px h-30px py-0 fw-500"
                  aria-controls="Search"
                >
                  {t(" + Create Manual Results")}
                </button>
              ) : null}
            </div>
          </div>
          <div
            className="col-xl-12 col-lg-12 col-md-12 col-sm-12 rounded mb-4"
            style={{ border: "2px solid #ffc700" }}
          >
            <div className="card mb-4 border">
              <div className="card-header bg-light-warning d-flex justify-content-between align-items-center px-4 min-h-40px">
                <h5 className="m-0 text-warning">{t("Final Results")}</h5>
              </div>
              <div className="card-body py-md-4 py-3 px-4">
                <div className="table_bordered overflow-hidden">
                  <TableContainer className="shadow-none">
                    <Table
                      stickyHeader
                      aria-label="sticky table collapsible"
                      className="plate-mapping-table mb-1"
                    >
                      <TableHead className="h-35px">
                        <TableRow>
                          <TableCell className="min-w-250px w-250px">
                            {t("Test")}
                          </TableCell>
                          <TableCell className="min-w-200px w-200px">
                            {t("Result")}
                          </TableCell>
                          <TableCell>{t("Interpretation")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleResultSummaryRows.map((summaryRow: any) => (
                          <TableRow key={summaryRow.fieldKey}>
                            <TableCell>
                              {t(getResultSummaryTestLabel(summaryRow))}
                            </TableCell>
                            <TableCell>
                              <input
                                type="text"
                                autoComplete="off"
                                className="form-control bg-transparent"
                                value={summaryRow.result}
                                onChange={(e) =>
                                  handleResultSummaryChange(
                                    summaryRow.fieldKey,
                                    e.target.value
                                  )
                                }
                                disabled={!isResultFieldEditable}
                              />
                            </TableCell>
                            <TableCell>
                              {t(summaryRow.interpretation)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </div>
          </div>
          {mPathogensList?.map((item: any, panelIndex: any) => (
            <div
              className="col-xl-12 col-lg-12 col-md-12 col-sm-12 rounded mt-5"
              style={{ border: "2px solid #ffc700" }}
              key={item.id}
            >
              <div className="card border">
                <div className="card-body py-md-4 py-3 px-4">
                  <div className="d-flex gap-2 mb-2">
                    <button
                      id="ResultDataExpandFillUndetermined"
                      className="btn btn-success btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                      onClick={() =>
                        ChangeOrganismStatus("Undetermined", item, panelIndex)
                      }
                    >
                      <span>{t("Fill Undetermined")}</span>
                    </button>
                  </div>
                  <h4 className="text-warning">{item?.panelName}</h4>
                  <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
                    <div className="table_bordered overflow-hidden position-relative">
                      <TableContainer className="shadow-none">
                        <Table
                          stickyHeader
                          aria-label="sticky table collapsible"
                          className="plate-mapping-table mb-1"
                        >
                          <TableHead className="h-40px">
                            <TableRow>
                              <TableCell className="min-w-250px w-250px">
                                {t("Organism")}
                              </TableCell>

                              <TableCell className="min-w-125px w-125px">
                                {t("CT Value")}
                              </TableCell>

                              <TableCell className="min-w-100px w-100px text-nowrap">
                                <label className="form-check form-check-inline form-check-solid m-0 fw-500 d-flex align-items-center flex-nowrap gap-2">
                                  <input
                                    id="ResultDataExpandWoundPanelReRun2CheckBox"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={
                                      item?.organisms.length > 0 &&
                                      item?.organisms.length ===
                                        mainCheckboxes.organismCheckbox.length
                                    }
                                    onChange={(e) =>
                                      handleSetReRunForAll(
                                        e.target.checked,
                                        panelIndex,
                                        "organism"
                                      )
                                    }
                                  />
                                  {t("Re-Run")}
                                </label>
                              </TableCell>
                              <TableCell className="min-w-100px w-100px">
                                {t("Flag")}
                              </TableCell>
                              <TableCell className="min-w-250px w-250px">
                                {t("Comments")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody className="scroll-start">
                            {loading ? (
                              <TableCell colSpan={5} className="padding-0">
                                <Loader />
                              </TableCell>
                            ) : (
                              item?.organisms?.map(
                                (data: any, organismIndex: any) => (
                                  <TableRow key={data.id}>
                                    <TableCell
                                      id={`WoundPanelOrganism_${
                                        organismIndex + 1
                                      }`}
                                    >
                                      {data?.organism}
                                    </TableCell>

                                    <TableCell
                                      id={`WoundPanelCTValue_${
                                        organismIndex + 1
                                      }`}
                                    >
                                      <input
                                        id={`WoundPanelCTValueInput_${
                                          organismIndex + 1
                                        }`}
                                        type="text"
                                        autoComplete="off"
                                        className="form-control bg-transparent"
                                        value={data?.ctValue ?? ""}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            panelIndex,
                                            organismIndex,
                                            "ctValue",
                                            e.target.value,
                                            "organism"
                                          )
                                        }
                                        onKeyDown={(e) => {
                                          if (!isNumericKey(e.key)) {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    </TableCell>

                                    <TableCell className="min-w-100px w-100px">
                                      <label className="form-check form-check-inline form-check-solid m-0 fw-500">
                                        <input
                                          id={`WoundPanelIsReRunCheckBox_${
                                            organismIndex + 1
                                          }`}
                                          className="form-check-input"
                                          type="checkbox"
                                          value={data?.isReRun}
                                          checked={data?.isReRun}
                                          onChange={(e) =>
                                            handleFieldChange(
                                              panelIndex,
                                              organismIndex,
                                              "isReRun",
                                              e.target.checked,
                                              "organism"
                                            )
                                          }
                                        />
                                      </label>
                                    </TableCell>
                                    <TableCell
                                      id={`WoundPanelFlag_${organismIndex + 1}`}
                                      className="min-w-100px w-100px"
                                    >
                                      {data?.flag ?? ""}
                                    </TableCell>
                                    <TableCell className="min-w-250px w-250px">
                                      <input
                                        id={`WoundPaneorganism_${
                                          organismIndex + 1
                                        }`}
                                        type="text"
                                        autoComplete="off"
                                        className="form-control bg-transparent"
                                        value={data?.comments}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            panelIndex,
                                            organismIndex,
                                            "comments",
                                            e.target.value,
                                            "organism"
                                            // false,
                                          )
                                        }
                                      />
                                    </TableCell>
                                  </TableRow>
                                )
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div
            className="col-xl-12 col-lg-12 col-md-12 col-sm-12 rounded mt-5"
            style={{ border: "2px solid #ffc700" }}
          >
            <div className="card border">
              <div className="card-body py-md-4 py-3 px-4">
                <label
                  htmlFor="ResultDataExpandInternalNotes"
                  className="text bold"
                >
                  {t("Internal Notes:")}
                </label>
                <textarea
                  id="ResultDataExpandInternalNotes"
                  className="form-control  h-80px mt-2"
                  name="lisNotes" // Name attribute for the text box
                  placeholder={t("Enter your Internal notes here")}
                  rows={5} // Increase the number of rows to increase the height
                  value={reportNotes.lisNotes}
                  onChange={(e) => handleFieldChangeTextArea(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="mt-2 py-4 d-flex mb-5 gap-2">
            {filterData.tabId === 1 ||
            (filterData.tabId === 2 && reportCheckboxes.isAmended) ? (
              <div className="d-flex gap-2">
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Publish"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <LoadButton
                      id={`ResultDataExpandPublish`}
                      className="btn btn-success btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                      loading={isPublishing}
                      btnText={getPermissionDisplayName(
                        pageName,
                        moduleName,
                        permissionIdentifier,
                        "Publish"
                      )}
                      loadingText={t("Processing.....")}
                      onClick={async () => {
                        if (
                          hasPermission === true &&
                          row?.graphImageFileBit === false
                        ) {
                          setShow(true);
                        } else {
                          await Publish(
                            requisitionId,
                            reqTypeId,
                            facilityId,
                            row?.requisitionOrderId
                          );
                        }
                      }}
                    />
                  )}
                </PermissionComponent>
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Validate"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <LoadButton
                      id={`ResultDataExpandValidate`}
                      className="btn btn-success btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                      loading={isValidating}
                      btnText={getPermissionDisplayName(
                        pageName,
                        moduleName,
                        permissionIdentifier,
                        "Validate"
                      )}
                      loadingText={t("Processing.....")}
                      onClick={async () => {
                        if (
                          hasPermission === true &&
                          row?.graphImageFileBit === false
                        ) {
                          setShow(true);
                        } else {
                          await Validate(
                            requisitionId,
                            reqTypeId,
                            facilityId,
                            row?.requisitionOrderId
                          );
                        }
                      }}
                    />
                  )}
                </PermissionComponent>
              </div>
            ) : null}
            <PermissionComponent
              moduleName="Oncology LIS"
              pageName="Result Data"
              permissionIdentifier="Preview"
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <LoadButton
                  id={`ResultDataExpandPreviewButton`}
                  className="btn btn-warning btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500 text-white"
                  loading={isPreviewing}
                  btnText={getPermissionDisplayName(
                    pageName,
                    moduleName,
                    permissionIdentifier,
                    "Preview"
                  )}
                  loadingText={t("Previewing")}
                  onClick={() =>
                    OncologyLISReportView(
                      requisitionId,
                      reqTypeId,
                      facilityId,
                      row.requisitionOrderId
                    )
                  }
                />
              )}
            </PermissionComponent>
            <PermissionComponent
              moduleName="Oncology LIS"
              pageName="Result Data"
              permissionIdentifier="Save"
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <LoadButton
                  id={`ResultDataExpandSaveButton`}
                  className="btn btn-primary btn-sm fw-bold search d-block fs-12px h-30px py-0 fw-500"
                  loading={isSubmitting}
                  btnText={getPermissionDisplayName(
                    pageName,
                    moduleName,
                    permissionIdentifier,
                    "Save"
                  )}
                  loadingText={t("Saving")}
                  onClick={() => SaveIdResultDataExpand()}
                />
              )}
            </PermissionComponent>
          </div>
        </div>
      </div>
      <Modal
        show={showEditFinalScoreModal}
        onHide={() => setShowEditFinalScoreModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="py-4">
          <Modal.Title className="h5">{t("Edit Final Scores")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to Edit the final scores manually? ")}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <button
            id="ResultDataExpandEditFinalScoreCancel"
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => setShowEditFinalScoreModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            id="ResultDataExpandEditFinalScoreConfirm"
            type="button"
            className="btn btn-sm btn-warning"
            onClick={confirmEditFinalScore}
          >
            {t("Confirm")}
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={show}
        onHide={ModalhandleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Publish & Validate")}</h4>
        </Modal.Header>
        <Modal.Body>
          {t("Graph file is not uploaded yet, do you want to Continue ?")}
        </Modal.Body>
        <Modal.Footer className="p-0">
          <button
            id={`IDResultDataModalPublishAndValidateCancel`}
            type="button"
            className="btn btn-secondary"
            onClick={ModalhandleClose}
          >
            {t("Cancel")}
          </button>
          <button
            id={`IDResultDataModalPublishAndValidateContinue`}
            type="button"
            className="btn btn-primary m-2"
            onClick={async () => {
              await Publish(
                requisitionId,
                reqTypeId,
                facilityId,
                row?.requisitionOrderId
              );
            }}
          >
            {t("Continue")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default memo(ResultDataExpandableRow);
